import {getTestProps} from '@benchling/js-test-utils';
import {Alert, Anchor, Button, Group, Stack, Text, createStyles} from '@mantine/core';
import {Dropzone} from '@mantine/dropzone';
import React, {type MutableRefObject, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {initiateTextDownload} from 'util/TextToBlobUtil';

import {buildInputsCsvTemplate} from './file-import/delimitedTextFileImporter';
import {FileImportError, type FileImportResult} from './file-import/FileImport';
import {ACCEPTED_FILE_TYPES, importFile} from './file-import/fileImporters';

interface InputsUploadDropzoneProps {
  disabled?: boolean;
  maxInputs: number;
  // Lets the Add-inputs menu open the file picker without a visible drop target.
  openRef: MutableRefObject<() => void>;
  onEnterManually: () => void;
  onError: (title: string, message: string) => void;
  onImported: (result: FileImportResult) => void;
  // When true, render the large empty-state target; otherwise the dropzone stays mounted but hidden.
  showEmptyState: boolean;
  usedInputNames?: ReadonlySet<string>;
}

export default function InputsUploadDropzone({
  disabled = false,
  maxInputs,
  openRef,
  onEnterManually,
  onError,
  onImported,
  showEmptyState,
  usedInputNames = new Set(),
}: InputsUploadDropzoneProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [isLoading, setIsLoading] = useState(false);

  const handleDrop = async (files: Array<File>): Promise<void> => {
    const file = files[0];
    if (file == null) {
      return;
    }
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.RUN_CONFIG_INPUT_METHOD_SELECTED_UPLOAD, {fileType: file.type});
    setIsLoading(true);
    try {
      onImported(await importFile(file, maxInputs, usedInputNames));
    } catch (error) {
      if (error instanceof FileImportError) {
        onError(error.title, error.message);
      } else {
        throw error;
      }
    } finally {
      setIsLoading(false);
    }
  };

  // The anchors live inside the Dropzone's inner wrapper, which Mantine renders with
  // `pointer-events: none`; the `classes.interactive` override below re-enables pointer events on the
  // anchors so these clicks reach them. We stop propagation so they don't bubble to the Dropzone root
  // and open the file picker.
  const handleDownloadTemplate = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_IMPORT_TEMPLATE_DOWNLOADED);
    initiateTextDownload(buildInputsCsvTemplate(), 'model-hub-multimer-template.csv');
  };

  const handleEnterManually = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.RUN_CONFIG_INPUT_METHOD_SELECTED_MANUAL);
    onEnterManually();
  };

  return (
    <Dropzone
      {...getTestProps({component: 'InputsUploadDropzone', element: 'root'})}
      accept={ACCEPTED_FILE_TYPES}
      activateOnClick={!disabled}
      activateOnDrag={!disabled}
      className={cx(classes.dropzone, {
        [classes.collapsed]: !showEmptyState && !isLoading,
        [classes.loadingCollapsed]: !showEmptyState && isLoading,
        [classes.emptyState]: showEmptyState,
      })}
      disabled={disabled || isLoading}
      loading={isLoading}
      multiple={false}
      onDrop={handleDrop}
      openRef={openRef}
    >
      {isLoading && (
        <Text aria-label="Importing file..." className={classes.srOnly} role="status">
          Importing file...
        </Text>
      )}
      {/* The dropzone stays mounted so the menu's "Upload file" can open the picker via openRef, but
          the large target only renders as the empty state when there are no inputs yet. */}
      {showEmptyState && (
        <Stack align="center" className={classes.target} spacing="md">
          <BenchlingIcon className={classes.icon} icon="upload" />
          <Text className={classes.primaryText} size="md" ta="center">
            Upload a CSV, TSV, or FASTA file, or{' '}
            <Anchor
              className={classes.interactive}
              component="button"
              onClick={handleEnterManually}
              type="button"
            >
              enter components manually
            </Anchor>
          </Text>
          <Alert
            aria-label="Template help"
            className={classes.interactive}
            classNames={{message: classes.templateAlertMessage, root: classes.templateAlert}}
            color="blue"
            icon={false}
            role="region"
            variant="light"
          >
            <Group align="center" className={classes.interactive} noWrap={true} position="apart" spacing="md">
              <Text>Need a starting point?</Text>
              <Button
                leftIcon={<BenchlingIcon icon="download" />}
                onClick={handleDownloadTemplate}
                size="sm"
                variant="primary"
              >
                Download multimer template
              </Button>
            </Group>
          </Alert>
        </Stack>
      )}
    </Dropzone>
  );
}

const useStyles = createStyles((theme) => ({
  dropzone: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  emptyState: {
    minHeight: 280,
  },
  // When inputs already exist the dropzone stays mounted (so openRef works) but takes no space.
  collapsed: {
    border: 'none',
    minHeight: 0,
    padding: 0,
  },
  // Give Mantine's built-in LoadingOverlay room to render when importing with existing inputs.
  loadingCollapsed: {
    border: 'none',
    minHeight: 48,
    padding: 0,
  },
  srOnly: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
  target: {
    padding: `${theme.spacing.xl} ${theme.spacing.md}`,
    width: '100%',
  },
  primaryText: {
    maxWidth: 520,
  },
  templateAlert: {
    width: '100%',
  },
  templateAlertMessage: {
    margin: 0,
    width: '100%',
  },
  // Mantine's Dropzone sets `pointer-events: none` on its inner wrapper so clicks fall through to the
  // root (which opens the file picker). Re-enable it on interactive descendants so their handlers
  // run; a descendant with `pointer-events: auto` stays clickable even when an ancestor disables it.
  interactive: {
    pointerEvents: 'auto',
  },
  icon: {
    color: theme.colors.gray[5],
    fontSize: 32,
  },
}));
